import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { animate, AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ArrowDownRight, ArrowUpRight, Check, Download } from 'lucide-react';
import { motionTokens } from '../motion-tokens';
import '../arc-foundation.css';
import styles from './metrics-dashboard.module.css';

export interface MetricsDashboardPoint {
  label: string;
  value: number;
  previous?: number | null;
}

export interface MetricsDashboardMetric {
  key: string;
  label: string;
  value: number;
  delta?: number | null;
  deltaLabel?: string;
  deltaDirection?: 'up' | 'down' | 'neutral';
  lowerIsBetter?: boolean;
  points: MetricsDashboardPoint[];
  format: (value: number) => string;
  axisFormat?: (value: number) => string;
}

export interface MetricsDashboardBreakdown {
  title: string;
  column: string;
  rows: Array<{ name: string; value: number; format?: (value: number) => string }>;
}

export interface MetricsDashboardSegment {
  value: string;
  label: string;
  ariaLabel?: string;
}

export interface MetricsDashboardProps {
  title: string;
  subtitle: string;
  metrics: MetricsDashboardMetric[];
  defaultMetric?: string;
  metricValue?: string;
  onMetricChange?: (value: string) => void;
  segments?: MetricsDashboardSegment[];
  segmentValue?: string;
  onSegmentChange?: (value: string) => void;
  segmentLabel?: string;
  compareLabel?: string;
  compareSeriesLabel?: string;
  breakdowns?: MetricsDashboardBreakdown[];
  actionLabel?: string;
  actionDoneLabel?: string;
  onAction?: () => void;
  ariaLabel?: string;
}

function CountValue({ value, format }: { value: number; format: (value: number) => string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef(value);
  const formatRef = useRef(format);
  const [initial] = useState(() => format(value));
  const reduce = useReducedMotion();
  useEffect(() => { formatRef.current = format; });
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (reduce) {
      shown.current = value;
      node.textContent = formatRef.current(value);
      return;
    }
    const controls = animate(shown.current, value, {
      duration: motionTokens.duration.considered,
      ease: motionTokens.ease.standard,
      onUpdate: (next) => {
        shown.current = next;
        node.textContent = formatRef.current(next);
      },
    });
    return () => controls.stop();
  }, [value, reduce]);
  return <span ref={ref}>{initial}</span>;
}

function smoothPath(points: [number, number][]) {
  if (points.length < 3) return points.map(([x, y], index) => (index ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)).join(' ');
  const count = points.length;
  const slope = points.slice(1).map(([x, y], index) => (y - points[index]![1]) / (x - points[index]![0]));
  const tangent = points.map((_, index) => {
    if (index === 0) return slope[0]!;
    if (index === count - 1) return slope[count - 2]!;
    const before = slope[index - 1]!;
    const after = slope[index]!;
    return before * after <= 0 ? 0 : (2 * before * after) / (before + after);
  });
  let path = `M${points[0]![0].toFixed(1)} ${points[0]![1].toFixed(1)}`;
  for (let index = 0; index < count - 1; index++) {
    const [x0, y0] = points[index]!;
    const [x1, y1] = points[index + 1]!;
    const handle = (x1 - x0) / 3;
    path += ` C${(x0 + handle).toFixed(1)} ${(y0 + tangent[index]! * handle).toFixed(1)} ${(x1 - handle).toFixed(1)} ${(y1 - tangent[index + 1]! * handle).toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  }
  return path;
}

function niceScale(min: number, max: number) {
  const safeMin = Number.isFinite(min) ? min : 0;
  const safeMax = Number.isFinite(max) ? max : 1;
  const padding = Math.max((safeMax - safeMin) * .18, Math.abs(safeMax) * .04, 1);
  const low = Math.min(0, safeMin - padding);
  const high = safeMax + padding;
  const span = high - low || 1;
  const raw = span / 3;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((value) => value * magnitude).find((value) => value >= raw) ?? raw;
  const start = Math.floor(low / step) * step;
  return { min: start, max: start + step * 3, ticks: [0, 1, 2, 3].map((index) => start + step * index) };
}

const WIDTH = 600;
const HEIGHT = 180;
const integerFormat = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 });

export function MetricsDashboard({
  title,
  subtitle,
  metrics,
  defaultMetric,
  metricValue,
  onMetricChange,
  segments,
  segmentValue,
  onSegmentChange,
  segmentLabel = 'Období',
  compareLabel = 'Srovnat',
  compareSeriesLabel = 'Předchozí období',
  breakdowns,
  actionLabel,
  actionDoneLabel = 'Hotovo',
  onAction,
  ariaLabel = title,
}: MetricsDashboardProps) {
  const reduce = useReducedMotion();
  const uid = useId();
  const initialMetric = defaultMetric && metrics.some((metric) => metric.key === defaultMetric) ? defaultMetric : metrics[0]?.key ?? '';
  const [metricKey, setMetricKey] = useState(initialMetric);
  const [compare, setCompare] = useState(false);
  const [active, setActive] = useState<number | null>(null);
  const [actionDone, setActionDone] = useState(false);
  const actionTimer = useRef<number | undefined>(undefined);
  const rootRef = useRef<HTMLElement>(null);
  const [size, setSize] = useState<'lg' | 'md' | 'sm'>('lg');

  useEffect(() => {
    if (!metrics.some((metric) => metric.key === metricKey)) setMetricKey(metrics[0]?.key ?? '');
  }, [metricKey, metrics]);

  useLayoutEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const measure = (width: number) => setSize(width < 460 ? 'sm' : width < 640 ? 'md' : 'lg');
    measure(node.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) measure(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => window.clearTimeout(actionTimer.current), []);

  const selectedMetricKey = metricValue ?? metricKey;
  const metric = metrics.find((item) => item.key === selectedMetricKey) ?? metrics[0];
  const points = metric?.points.length ? metric.points : [{ label: '-', value: metric?.value ?? 0, previous: null }];
  const hasPrevious = points.some((point) => point.previous != null);

  const chart = useMemo(() => {
    const values = points.flatMap((point) => compare && point.previous != null ? [point.value, point.previous] : [point.value]);
    const scale = niceScale(Math.min(...values), Math.max(...values));
    const x = (index: number) => points.length === 1 ? WIDTH / 2 : (index / (points.length - 1)) * WIDTH;
    const y = (value: number) => HEIGHT - ((value - scale.min) / (scale.max - scale.min || 1)) * HEIGHT;
    const line = (key: 'value' | 'previous') => smoothPath(points.map((point, index) => [x(index), y(key === 'value' ? point.value : point.previous ?? point.value)]));
    const current = line('value');
    return { scale, x, y, current, previous: line('previous'), area: `${current} L${WIDTH} ${HEIGHT} L0 ${HEIGHT} Z` };
  }, [compare, points]);

  if (!metric) return null;

  const labelEvery = Math.max(1, Math.ceil(points.length / (size === 'sm' ? 4 : size === 'md' ? 5 : 6)));
  const focus = active === null ? null : points[active];
  const axisFormat = metric.axisFormat ?? ((value: number) => integerFormat.format(Math.round(value)));
  const swap = reduce ? { duration: 0 } : { duration: motionTokens.duration.fast, ease: motionTokens.ease.standard };
  const rescale = reduce ? { duration: 0 } : motionTokens.spring.smooth;
  const tooltipLeft = focus ? Math.min(88, Math.max(12, (chart.x(active!) / WIDTH) * 100)) : 0;

  function inspect(event: PointerEvent<HTMLDivElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    setActive(Math.round(ratio * (points.length - 1)));
  }

  function onChartKey(event: KeyboardEvent<HTMLDivElement>) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const last = points.length - 1;
    setActive((current) => {
      if (event.key === 'Home') return 0;
      if (event.key === 'End') return last;
      const from = current ?? last;
      return Math.min(last, Math.max(0, from + (event.key === 'ArrowRight' ? 1 : -1)));
    });
  }

  function runAction() {
    onAction?.();
    setActionDone(true);
    window.clearTimeout(actionTimer.current);
    actionTimer.current = window.setTimeout(() => setActionDone(false), 1800);
  }

  return (
    <section ref={rootRef} className={styles.dashboard} data-size={size} aria-label={ariaLabel}>
      <header className={styles.header}>
        <div className={styles.title}><h2>{title}</h2><p>{subtitle}</p></div>
        {(segments?.length || actionLabel) && (
          <div className={styles.actions}>
            {segments?.length ? (
              <div className={styles.segmented} role="group" aria-label={segmentLabel}>
                {segments.map((segment) => (
                  <button key={segment.value} type="button" aria-pressed={segmentValue === segment.value} aria-label={segment.ariaLabel ?? segment.label} onClick={() => onSegmentChange?.(segment.value)}>
                    {segmentValue === segment.value && <motion.span layoutId={`${uid}-segment`} className={styles.segmentThumb} transition={reduce ? { duration: 0 } : motionTokens.spring.snappy} />}
                    <span className={styles.segmentLabel}>{segment.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {actionLabel ? (
              <button type="button" className={styles.exportButton} onClick={runAction} aria-live="polite">
                <span className={styles.exportIcon}>
                  <AnimatePresence initial={false} mode="popLayout">
                    <motion.span key={actionDone ? 'done' : 'idle'} initial={reduce ? false : { opacity: 0, scale: .8 }} animate={{ opacity: 1, scale: 1 }} exit={reduce ? { opacity: 0 } : { opacity: 0, scale: .8 }} transition={swap}>
                      {actionDone ? <Check size={14} /> : <Download size={14} />}
                    </motion.span>
                  </AnimatePresence>
                </span>
                <span className={styles.exportLabel}>
                  <span aria-hidden={actionDone} data-hidden={actionDone}>{actionLabel}</span>
                  <span aria-hidden={!actionDone} data-hidden={!actionDone}>{actionDoneLabel}</span>
                </span>
              </button>
            ) : null}
          </div>
        )}
      </header>

      <div className={styles.kpis} role="tablist" aria-label="Metrika">
        {metrics.map((item) => {
          const selected = metric.key === item.key;
          const direction = item.deltaDirection ?? (item.delta == null || item.delta === 0 ? 'neutral' : item.delta > 0 ? 'up' : 'down');
          const good = direction === 'neutral' || (item.lowerIsBetter ? direction === 'down' : direction === 'up');
          return (
            <button key={item.key} type="button" role="tab" aria-selected={selected} className={styles.kpi} onClick={() => { onMetricChange?.(item.key); if (!onMetricChange) setMetricKey(item.key); setActive(null); }}>
              {selected && <motion.span layoutId={`${uid}-kpi`} className={styles.kpiIndicator} transition={reduce ? { duration: 0 } : motionTokens.spring.smooth} />}
              <span className={styles.kpiLabel}>{item.label}</span>
              <span className={styles.kpiValue}><CountValue value={item.value} format={item.format} /></span>
              <span className={`${styles.delta} ${good ? styles.good : styles.bad}`} data-neutral={direction === 'neutral'}>
                {direction === 'up' ? <ArrowUpRight size={13} aria-hidden /> : direction === 'down' ? <ArrowDownRight size={13} aria-hidden /> : null}
                <span>{item.deltaLabel ?? (item.delta == null ? 'bez srovnání' : `${Math.abs(item.delta).toFixed(1)} %`)}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className={styles.chartSection}>
        <div className={styles.chartBar}>
          <div className={styles.legend} aria-hidden="true">
            <span><i className={styles.swatch} />{metric.label}</span>
            {compare && hasPrevious ? <span><i className={styles.swatchPrevious} />{compareSeriesLabel}</span> : null}
          </div>
          {hasPrevious ? (
            <label className={styles.compare}>
              <input type="checkbox" checked={compare} onChange={(event) => setCompare(event.target.checked)} />
              <span className={styles.check} aria-hidden="true"><Check size={11} strokeWidth={2.5} /></span>
              {compareLabel}
            </label>
          ) : null}
        </div>

        <div className={styles.plot}>
          <div className={styles.yAxis} aria-hidden="true">{[...chart.scale.ticks].reverse().map((tick) => <span key={tick}>{axisFormat(tick)}</span>)}</div>
          <div
            className={styles.chartArea}
            tabIndex={0}
            role="img"
            aria-label={`${metric.label}. Pomocí šipek lze procházet hodnoty grafu.`}
            onPointerMove={inspect}
            onPointerDown={inspect}
            onPointerLeave={(event) => { if (event.pointerType === 'mouse') setActive(null); }}
            onKeyDown={onChartKey}
            onBlur={() => setActive(null)}
          >
            <div className={styles.grid} aria-hidden="true">{chart.scale.ticks.map((tick) => <span key={tick} />)}</div>
            <AnimatePresence initial={false}>
              <motion.svg key={`${segmentValue ?? ''}-${metric.key}`} className={styles.svg} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" aria-hidden="true" initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduce ? 0 : motionTokens.duration.standard, ease: motionTokens.ease.standard }}>
                <motion.path className={styles.area} initial={false} animate={{ d: chart.area }} transition={rescale} />
                <motion.path className={styles.previous} initial={false} animate={{ d: chart.previous, opacity: compare && hasPrevious ? 1 : 0 }} transition={rescale} />
                <motion.path className={styles.line} initial={false} animate={{ d: chart.current }} transition={rescale} />
              </motion.svg>
            </AnimatePresence>
            {focus ? (
              <>
                <span className={styles.crosshair} style={{ left: `${(chart.x(active!) / WIDTH) * 100}%` }} aria-hidden="true" />
                <span className={styles.dot} style={{ left: `${(chart.x(active!) / WIDTH) * 100}%`, top: `${(chart.y(focus.value) / HEIGHT) * 100}%` }} aria-hidden="true" />
                <div className={styles.tooltip} style={{ left: `${tooltipLeft}%`, ...(chart.y(focus.value) / HEIGHT < .5 ? { top: 'auto', bottom: 6 } : null) }} role="status">
                  <span className={styles.tooltipDate}>{focus.label}</span>
                  <strong>{metric.format(focus.value)}</strong>
                  {compare && focus.previous != null ? <span className={styles.tooltipPrevious}>{metric.format(focus.previous)} · {compareSeriesLabel.toLowerCase()}</span> : null}
                </div>
              </>
            ) : null}
          </div>
          <div className={styles.xAxis} aria-hidden="true">
            {points.map((point, index) => {
              if (index % labelEvery !== 0) return null;
              const percent = (chart.x(index) / WIDTH) * 100;
              return <span key={`${point.label}-${index}`} style={{ left: `${percent}%`, transform: `translateX(${index === 0 ? 0 : percent > 92 ? -100 : -50}%)` }}>{point.label}</span>;
            })}
          </div>
        </div>
      </div>

      {breakdowns?.length ? (
        <div className={styles.breakdowns}>
          {breakdowns.slice(0, 2).map((breakdown) => <Breakdown key={breakdown.title} breakdown={breakdown} reduce={!!reduce} />)}
        </div>
      ) : null}
    </section>
  );
}

function Breakdown({ breakdown, reduce }: { breakdown: MetricsDashboardBreakdown; reduce: boolean }) {
  const max = Math.max(1, ...breakdown.rows.map((row) => Math.abs(row.value)));
  return (
    <section className={styles.breakdown} aria-label={breakdown.title}>
      <div className={styles.breakdownHead}><h3>{breakdown.title}</h3><span>{breakdown.column}</span></div>
      <ul>
        {breakdown.rows.map((row) => (
          <li key={row.name}>
            <span className={styles.bar} style={{ clipPath: `inset(0 ${(100 - (Math.abs(row.value) / max) * 100).toFixed(2)}% 0 0 round 6px)`, transitionDuration: reduce ? '0ms' : undefined }} aria-hidden="true" />
            <span className={styles.rowName} title={row.name}>{row.name}</span>
            <span className={styles.rowValue}><CountValue value={row.value} format={row.format ?? ((value) => integerFormat.format(Math.round(value)))} /></span>
          </li>
        ))}
      </ul>
    </section>
  );
}
